'use client';

import { useTenant } from '@/contexts/TenantContext';
import { analyticsEvents } from '@/components/analytics/GoogleAnalytics';
import { WhatsAppWidget } from '@routeroyal/ui-core';
import type { WhatsAppConfig } from '@/lib/api/types';

/**
 * Hardcoded WhatsApp defaults per theme.
 * These are used when the Route Royal API does not yet provide a whatsapp config.
 * Once the API returns a config, it takes precedence over these defaults.
 */
const THEME_WHATSAPP_DEFAULTS: Record<string, WhatsAppConfig> = {
  allys: {
    enabled: true,
    defaultMessage: "Hi, I'm interested in your properties in Mauritius.",
    position: 'bottom-right',
    showOnMobile: true,
    agents: [
      {
        name: 'Yalda Sheri',
        phoneNumber: '23052584239',
        department: 'Senior Property Consultant',
        avatar: 'https://media.routeroyal.com/XmN50nxBvldetj3mYWesFTu4Ct4o0aAFOhdEVbShzEA/raw:1/czM6Ly9jb2xsYXBzaWJsZS1wdXJzZS1kbW9rLWRubDd4cy9yb3V0ZXJveWFsL3JveWFsZS9zdGF0aWMvZDBlNjE1ZmI5MmY1MzQwMGE5YWI5YmRlMTY2NjcyZWU5MDQ0YmRhODQyY2MwMzU3NjdmMjRhMjA4YWJiZGY3NS5wbmc.png',
        availabilityText: 'Typically replies within 1 hour',
      },
      {
        name: 'Junaid Nuzeebun',
        phoneNumber: '23052584240',
        department: 'Property Development Specialist',
        avatar: 'https://media.routeroyal.com/XQfAbSbLOraw6YGU1hnBjNvIzTiAW_3tXdQLmgpuBB8/raw:1/czM6Ly9jb2xsYXBzaWJsZS1wdXJzZS1kbW9rLWRubDd4cy9yb3V0ZXJveWFsL3JveWFsZS9zdGF0aWMvYThkYjA4YTM4MzMyYjczMDQzMWZkYTQ4MjA2MjliZDQ4ZGRiYjlkMTZlMTVjMDMxNzU2NjhlNTViNjY2NDYxZC5wbmc.png',
        availabilityText: 'Typically replies within 1 hour',
      },
    ],
  },
  'stone-investment': {
    enabled: true,
    defaultMessage: "Bonjour, I'm interested in learning more about your properties.",
    position: 'bottom-right',
    showOnMobile: true,
    agents: [
      {
        name: 'Pierre Monville',
        phoneNumber: '33616641604',
        department: 'Founder & Managing Director',
        avatar: 'https://media.routeroyal.com/7q939t5U8kw4KYfV0-f1Z4Ie3jx_2Z_bxhJmMwd2YrA/raw:1/czM6Ly9jb2xsYXBzaWJsZS1wdXJzZS1kbW9rLWRubDd4cy9yb3V0ZXJveWFsL3JveWFsZS9zdGF0aWMvZmVmMmU3ZWMzMGI0MjI3ODRjNjE4N2E2MTgzMDkwMjliMzM2YjlhYmY3ODllYWY5OTMyZjAyYzc0OThhYjZmYi5qcGc.jpg',
        availabilityText: 'Typically replies within a few hours',
      },
    ],
  },
  'stone-investments': {
    enabled: true,
    defaultMessage: "Bonjour, I'm interested in learning more about your properties.",
    position: 'bottom-right',
    showOnMobile: true,
    agents: [
      {
        name: 'Pierre Monville',
        phoneNumber: '33616641604',
        department: 'Founder & Managing Director',
        avatar: 'https://media.routeroyal.com/7q939t5U8kw4KYfV0-f1Z4Ie3jx_2Z_bxhJmMwd2YrA/raw:1/czM6Ly9jb2xsYXBzaWJsZS1wdXJzZS1kbW9rLWRubDd4cy9yb3V0ZXJveWFsL3JveWFsZS9zdGF0aWMvZmVmMmU3ZWMzMGI0MjI3ODRjNjE4N2E2MTgzMDkwMjliMzM2YjlhYmY3ODllYWY5OTMyZjAyYzc0OThhYjZmYi5qcGc.jpg',
        availabilityText: 'Typically replies within a few hours',
      },
    ],
  },
};

/**
 * WhatsApp Chat Widget - Tenant-aware wrapper
 *
 * Reads WhatsApp configuration from the tenant context and renders
 * the core WhatsAppWidget with analytics integration.
 * Falls back to hardcoded theme defaults when the API config is not yet available.
 */
export function WhatsAppChatWidget() {
  const { whatsapp, themeId } = useTenant();

  const config = whatsapp ?? THEME_WHATSAPP_DEFAULTS[themeId];

  if (!config?.enabled || !config.agents?.length) {
    return null;
  }

  const widgetAgents = config.agents.map((agent) => ({
    name: agent.name,
    phoneNumber: agent.phoneNumber,
    department: agent.department,
    avatar: agent.avatar,
    availabilityText: agent.availabilityText,
  }));

  return (
    <WhatsAppWidget
      agents={widgetAgents}
      defaultMessage={config.defaultMessage}
      position={config.position}
      onClickAgent={(phoneNumber, agentName) => {
        analyticsEvents.clickToWhatsApp(phoneNumber, `whatsapp_widget_${agentName}`);
      }}
    />
  );
}
